import { useState } from "react";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertTriangle, Clock, CalendarCheck, FileText, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { AddRenewalDialog, DocumentViewDialog } from "@/components/EntityDialogs";
import { toast } from "sonner";

const catColor = {
  Insurance: "bg-neutral-100 text-neutral-700",
  Travel: "bg-neutral-100 text-neutral-700",
  Immigration: "bg-amber-100 text-amber-700",
  Health: "bg-emerald-100 text-emerald-700",
  Education: "bg-rose-100 text-rose-700",
};

export default function Renewals() {
  const { renewals, documents, getPerson, resolveRenewal } = useFgp();
  const [addOpen, setAddOpen] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const sorted = [...renewals].sort((a, b) => new Date(a.due) - new Date(b.due));
  const docOf = (id) => documents.find((d) => d.id === id);
  const ownerName = (id) => id === "ws" ? "Household" : getPerson(id)?.name || "—";
  const openDoc = (id) => { const d = docOf(id); if (d) setViewDoc(d); };
  const markRenewed = (r) => { resolveRenewal(r.id); toast.success(`${r.title} renewed — rolled forward 12 months`); };

  return (
    <div>
      <PageHeader title="Renewals" subtitle="A single timeline for everything that lapses — never miss a date again."
        actions={<Button onClick={() => setAddOpen(true)} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="add-renewal"><RefreshCw className="h-4 w-4" /> Add renewal</Button>} />

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-6">Upcoming timeline</h2>
        <div className="relative pl-6">
          <div className="absolute left-2 top-1 bottom-1 w-px bg-slate-200" />
          <div className="space-y-5">
            {sorted.map((r, i) => {
              const dotColor = r.status === "overdue" ? "bg-rose-500" : r.status === "due-soon" ? "bg-amber-400" : "bg-neutral-500";
              return (
                <motion.div key={r.id} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                  className="relative" data-testid={`renewal-${r.id}`}>
                  <span className={`absolute -left-[18px] top-1.5 h-3 w-3 rounded-full ring-4 ring-white ${dotColor}`} />
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-lg border border-slate-200 p-4 hover:border-neutral-200 transition-colors">
                    <div className="text-center sm:w-16 flex-shrink-0">
                      <p className="text-xs text-slate-400">{new Date(r.due).toLocaleDateString("en-GB", { month: "short" })}</p>
                      <p className="font-heading text-xl font-semibold text-slate-800 leading-none">{new Date(r.due).getDate()}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{r.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${catColor[r.category] || "bg-slate-100 text-slate-600"}`}>{r.category}</span>
                        <span className="text-xs text-slate-400">{r.owner}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {r.amount !== "—" && <span className="text-sm font-medium text-slate-700">{r.amount}</span>}
                      <StatusBadge status={r.status} />
                      {(r.status === "overdue" || r.status === "due-soon") && (
                        <Button variant="outline" size="sm" onClick={() => markRenewed(r)} className="gap-1.5 flex-shrink-0" data-testid={`renew-${r.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Mark as renewed
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      <AddRenewalDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
